import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Chip, CircularProgress, Paper, Stack, Typography, Button, Divider, List, ListItem, ListItemText, IconButton, Card } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { fetchAnnouncementById, updateAnnouncementCoverImage, updateAnnouncementAttachments } from '../controllers/announcementsController';
import { fetchEventById } from '../controllers/clubEventsController';
import { formatTurkeyLocalDateTime } from '../utils/turkeyLocalDateTime';
import { fetchUserById } from '../controllers/userManagementController';
import EditAnnouncementDialog from './EditAnnouncementDialog';
import { resolveAnnouncementAssetUrl } from '../api/httpClientAnnouncements';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BackButton from '../components/BackButton';

const mapCategory = (v) => {
    switch (v) {
        case 1: return 'Etkinlik';
        case 2: return 'Proje';
        case 3: return 'Vefat';
        case 4: return 'Evlilik';
        case 5: return 'Doğum';
        case 6: return 'Yeni Üye Katılımı';
        case 7: return 'Bayram Tebrikleri';
        default: return 'Genel';
    }
};
const mapVisibility = (v) => (v === 1 ? 'Herkese Açık' : 'Sadece Üyeler');
const mapAutomation = (v) => (v === 1 ? 'Tek Sefer' : v === 2 ? 'Tekrarlayan' : 'Manuel');

const SEND_CHANNEL_LABELS = {
    1: 'E-posta',
    2: 'SMS',
    4: 'Mobil Uygulama Bildirimi',
    8: 'Mobil Uygulama',
};

const renderSendChannels = (mask) => {
    if (mask == null || mask === 0) return null;
    const items = [];
    if ((mask & 1) === 1) items.push(SEND_CHANNEL_LABELS[1]);
    if ((mask & 2) === 2) items.push(SEND_CHANNEL_LABELS[2]);
    if ((mask & 4) === 4) items.push(SEND_CHANNEL_LABELS[4]);
    if ((mask & 8) === 8) items.push(SEND_CHANNEL_LABELS[8]);
    return items.length ? items.join(', ') : null;
};

const AnnouncementDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [eventInfo, setEventInfo] = useState(null);
    const [creatorInfo, setCreatorInfo] = useState(null);
    const [creatorError, setCreatorError] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [updatingCoverImage, setUpdatingCoverImage] = useState(false);
    const [updatingAttachments, setUpdatingAttachments] = useState(false);
    const [newFiles, setNewFiles] = useState([]); // File objeleri
    const coverImageInputRef = useRef(null);
    const attachmentsInputRef = useRef(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        (async () => {
            try {
                const data = await fetchAnnouncementById(id);
                if (!active) return;
                setItem(data);
                setNewFiles([]);
                setCreatorInfo(null);
                setCreatorError('');
                if (data?.createdById) {
                    try {
                        const creator = await fetchUserById(data.createdById);
                        if (active) setCreatorInfo(creator);
                    } catch (err) {
                        if (active) setCreatorError(err?.message || 'Kullanıcı bilgisi alınamadı');
                    }
                }
                if (data?.category === 1 && data?.relatedEntityId) {
                    try {
                        const ev = await fetchEventById(data.relatedEntityId);
                        if (active) setEventInfo(ev);
                    } catch { }
                } else {
                    setEventInfo(null);
                }
            } catch (e) {
                if (!active) return;
                setError(e?.message || 'Duyuru alınamadı');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
    if (!item) return null;

    const creatorLabel = creatorInfo
        ? (creatorInfo.username || creatorInfo.fullName || creatorInfo.email || creatorInfo.id || String(item.createdById || ''))
        : String(item.createdById || '');

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day} ${month} ${year} - ${hours}:${minutes}`;
    };

    return (
        <Stack spacing={2}>
            <BackButton />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: 500 }}>{item.title}</Typography>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEditOpen(true)}
                    startIcon={<EditIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                >
                    Düzenle
                </Button>
            </Stack>
            <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: 'none' }}>
                <Stack spacing={3}>
                    {/* Kapak Görseli ve Bilgiler */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '.75fr 1fr' }, gap: 5, alignItems: 'center' }}>
                        {/* Sol Taraf - Kapak Görseli */}
                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>Kapak Görseli</Typography>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="cover-image-update"
                                    type="file"
                                    ref={coverImageInputRef}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        setUpdatingCoverImage(true);
                                        try {
                                            const newCoverImageUrl = await updateAnnouncementCoverImage(item.id, file);
                                            // Refresh announcement data
                                            const data = await fetchAnnouncementById(item.id);
                                            setItem(data);
                                        } catch (err) {
                                            alert(err?.message || 'Kapak görseli güncellenemedi');
                                        } finally {
                                            setUpdatingCoverImage(false);
                                            if (coverImageInputRef.current) {
                                                coverImageInputRef.current.value = '';
                                            }
                                        }
                                    }}
                                />
                                <Button
                                    component="label"
                                    htmlFor="cover-image-update"
                                    size="small"
                                    startIcon={updatingCoverImage ? <CircularProgress size={14} sx={{ color: '#0E4D94' }} /> : <EditIcon sx={{ width: 16 }} />}
                                    disabled={updatingCoverImage}
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: '#E7EDF4',
                                        boxShadow: 'none',
                                        fontSize: 13,
                                        borderRadius: '5px',
                                        px: 1,
                                        color: '#0E4D94',
                                        '&:hover': {
                                            backgroundColor: '#CFDBEA',
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    {updatingCoverImage ? 'Güncelleniyor...' : item.coverImageUrl ? 'Güncelle' : 'Ekle'}
                                </Button>
                            </Stack>
                            {item.coverImageUrl ? (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: 300,
                                        maxHeight: 400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '5px',
                                        border: '1px solid #e6e6e6',
                                        backgroundColor: '#fafafa',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={resolveAnnouncementAssetUrl(item.coverImageUrl)}
                                        alt="Kapak Görseli"
                                        sx={{
                                            width: 'auto',
                                            height: 'auto',
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        minHeight: 300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '5px',
                                        backgroundColor: '#fafafa',
                                        border: '2px dashed #e6e6e6',
                                    }}
                                >
                                    <Stack spacing={1} alignItems="center">
                                        <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#a3a3a3' }}>Kapak görseli eklenmemiş</Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Box>

                        {/* Sağ Taraf - Bilgiler */}
                        <Box>
                            <Stack spacing={2}>
                                {/* Kategori - Görünürlük */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <CategoryIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: .5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Kategori</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>{mapCategory(item.category)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <VisibilityIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: .5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Görünürlük</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>{mapVisibility(item.visibility)}</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Otomasyon - Kanallar */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <SettingsIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: .5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Otomasyon</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>{mapAutomation(item.automationType)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <SendIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: .5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Yayın Kanalları</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>{renderSendChannels(item.sendChannels) || '-'}</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Yayın/Bitiş - Durum */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <ScheduleIcon sx={{ fontSize: '20px', color: '#0E4D94', mt: .5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Yayınlama / Bitiş Tarihi</Typography>
                                            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#444444', mb: 0.25 }}>{item.publishAt ? formatDate(item.publishAt) : '-'}</Typography>
                                            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#a3a3a3' }}>{item.expireAt ? formatDate(item.expireAt) : '-'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        {item.isPublished ? (
                                            <CheckCircleIcon sx={{ fontSize: '20px', color: '#17C653', mt: .5 }} />
                                        ) : (
                                            <RadioButtonUncheckedIcon sx={{ fontSize: '20px', color: '#F8285A', mt: .5 }} />
                                        )}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.25 }}>Durum</Typography>
                                            <Chip
                                                label={item.isPublished ? 'Yayında' : 'Taslak'}
                                                size="small"
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    backgroundColor: item.isPublished ? '#DFFFEA' : '#FFEEF3',
                                                    color: item.isPublished ? '#17C653' : '#F8285A',
                                                    height: 24,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Stack>

                            {/* Ek Dosyalar */}
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5 }}>Ek Dosyalar</Typography>
                                {(item.attachments && Array.isArray(item.attachments) && item.attachments.length > 0) || newFiles.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {/* Dosyalar ve Dosya Ekle Butonu */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                            {/* Mevcut dosyalar */}
                                            {item.attachments && Array.isArray(item.attachments) && item.attachments.map((attachment, index) => {
                                                const fileUrl = resolveAnnouncementAssetUrl(attachment.fileLocation);
                                                return (
                                                    <Box
                                                        key={`existing-${index}`}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            px: 1.5,
                                                            py: 0.75,
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '5px',
                                                            border: '1px solid #e6e6e6',
                                                            maxWidth: '250px',
                                                        }}
                                                    >
                                                        <AttachFileIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '12px',
                                                                color: '#444444',
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                flex: 1,
                                                            }}
                                                        >
                                                            {attachment.fileName}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={fileUrl}
                                                            download={attachment.fileName}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                                borderRadius: '4px',
                                                                width: 20,
                                                                height: 20,
                                                                minWidth: 20,
                                                                padding: 0,
                                                                '&:hover': {
                                                                    backgroundColor: '#E7EDF4',
                                                                },
                                                                '& svg': {
                                                                    fontSize: '14px',
                                                                    color: '#0E4D94',
                                                                },
                                                            }}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={async () => {
                                                                // Bu dosya hariç diğer tüm dosyaları koru
                                                                const otherAttachments = item.attachments
                                                                    .filter((a, i) => i !== index)
                                                                    .map(a => a.fileLocation);

                                                                setUpdatingAttachments(true);
                                                                try {
                                                                    await updateAnnouncementAttachments(
                                                                        item.id,
                                                                        otherAttachments.length > 0 ? otherAttachments : [],
                                                                        null
                                                                    );
                                                                    // Refresh announcement data
                                                                    const data = await fetchAnnouncementById(item.id);
                                                                    setItem(data);
                                                                } catch (err) {
                                                                    alert(err?.message || 'Dosya silinemedi');
                                                                } finally {
                                                                    setUpdatingAttachments(false);
                                                                }
                                                            }}
                                                            disabled={updatingAttachments}
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                                borderRadius: '4px',
                                                                width: 20,
                                                                height: 20,
                                                                minWidth: 20,
                                                                padding: 0,
                                                                '&:hover': {
                                                                    backgroundColor: '#FFEEF3',
                                                                },
                                                                '& svg': {
                                                                    fontSize: '14px',
                                                                    color: '#F8285A',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}
                                            {/* Yeni eklenen dosyalar */}
                                            {newFiles.map((file, index) => (
                                                <Box
                                                    key={`new-${index}`}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '5px',
                                                        border: '1px solid #e6e6e6',
                                                        maxWidth: '250px',
                                                    }}
                                                >
                                                    <AttachFileIcon sx={{ fontSize: '14px', color: '#17C653' }} />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '12px',
                                                            color: '#17C653',
                                                            fontWeight: 500,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {file.name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setNewFiles((prev) => prev.filter((_, i) => i !== index));
                                                        }}
                                                        sx={{
                                                            backgroundColor: 'transparent',
                                                            borderRadius: '4px',
                                                            width: 20,
                                                            height: 20,
                                                            minWidth: 20,
                                                            padding: 0,
                                                            '&:hover': {
                                                                backgroundColor: '#FFEEF3',
                                                            },
                                                            '& svg': {
                                                                fontSize: '14px',
                                                                color: '#F8285A',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            {/* Dosya Ekle Butonu */}
                                            <Box
                                                component="label"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '5px',
                                                    border: '1px dashed #e6e6e6',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: '#0E4D94',
                                                        backgroundColor: '#f0f0f0',
                                                    },
                                                }}
                                            >
                                                <input
                                                    accept="*/*"
                                                    style={{ display: 'none' }}
                                                    type="file"
                                                    multiple
                                                    ref={attachmentsInputRef}
                                                    onChange={(e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        if (files.length > 0) {
                                                            setNewFiles((prev) => [...prev, ...files]);
                                                        }
                                                        if (attachmentsInputRef.current) {
                                                            attachmentsInputRef.current.value = '';
                                                        }
                                                    }}
                                                />
                                                <AttachFileIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#444444',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Dosya Ekle
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Onayla Butonu (sadece yeni dosya varsa) - Alt satırda sabit */}
                                        {newFiles.length > 0 && (
                                            <Box>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={async () => {
                                                        // Mevcut tüm dosyaları koru ve yeni dosyaları ekle
                                                        setUpdatingAttachments(true);
                                                        try {
                                                            const allCurrentFileLocations = item.attachments && Array.isArray(item.attachments)
                                                                ? item.attachments.map(a => a.fileLocation)
                                                                : [];
                                                            await updateAnnouncementAttachments(
                                                                item.id,
                                                                allCurrentFileLocations.length > 0 ? allCurrentFileLocations : null,
                                                                newFiles.length > 0 ? newFiles : null
                                                            );
                                                            // Refresh announcement data
                                                            const data = await fetchAnnouncementById(item.id);
                                                            setItem(data);
                                                            setNewFiles([]);
                                                        } catch (err) {
                                                            alert(err?.message || 'Ek dosyalar güncellenemedi');
                                                        } finally {
                                                            setUpdatingAttachments(false);
                                                        }
                                                    }}
                                                    disabled={updatingAttachments}
                                                    sx={{
                                                        textTransform: 'none',
                                                        backgroundColor: '#E7EDF4',
                                                        boxShadow: 'none',
                                                        fontSize: 13,
                                                        borderRadius: '5px',
                                                        px: 1.5,
                                                        py: 0.75,
                                                        color: '#0E4D94',
                                                        '&:hover': {
                                                            backgroundColor: '#CFDBEA',
                                                            boxShadow: 'none',
                                                        },
                                                    }}
                                                >
                                                    {updatingAttachments ? 'Güncelleniyor...' : 'Onayla'}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box
                                        component="label"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1.5,
                                            py: 0.75,
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '5px',
                                            border: '1px solid #e6e6e6',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            width: 'fit-content',
                                            '&:hover': {
                                                borderColor: '#0E4D94',
                                                backgroundColor: '#f0f0f0',
                                            },
                                        }}
                                    >
                                        <input
                                            accept="*/*"
                                            style={{ display: 'none' }}
                                            type="file"
                                            multiple
                                            ref={attachmentsInputRef}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                if (files.length > 0) {
                                                    setNewFiles((prev) => [...prev, ...files]);
                                                }
                                                if (attachmentsInputRef.current) {
                                                    attachmentsInputRef.current.value = '';
                                                }
                                            }}
                                        />
                                        <AttachFileIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '12px',
                                                color: '#444444',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Dosya Ekle
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: '#e6e6e6' }} />

                    {/* İçerik */}
                    <Box>
                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, mb: 1.5 }}>İçerik</Typography>
                        <Box
                            sx={{
                                '& p': { margin: '0.5em 0', fontSize: '14px', color: '#444444' },
                                '& h1, & h2, & h3': { marginTop: '0.5em', marginBottom: '0.5em', fontSize: '16px', fontWeight: 500 },
                                '& ul, & ol': { paddingLeft: '1.5em' },
                                '& blockquote': {
                                    borderLeft: '3px solid',
                                    borderColor: '#e6e6e6',
                                    paddingLeft: '1em',
                                    marginLeft: 0,
                                    fontStyle: 'italic',
                                },
                                '& code': {
                                    backgroundColor: '#f5f5f5',
                                    padding: '0.2em 0.4em',
                                    borderRadius: '3px',
                                    fontSize: '0.9em',
                                },
                                '& pre': {
                                    backgroundColor: '#f5f5f5',
                                    padding: '1em',
                                    borderRadius: '4px',
                                    overflow: 'auto',
                                },
                                '& img': {
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: '5px',
                                },
                                '& a': {
                                    color: '#0E4D94',
                                    textDecoration: 'underline',
                                },
                            }}
                            dangerouslySetInnerHTML={{ __html: item.content || '' }}
                        />
                    </Box>


                    {/* İlgili Etkinlik */}
                    {item.category === 1 && item.relatedEntityId && (
                        <>
                            <Divider sx={{ borderColor: '#e6e6e6' }} />
                            <Box>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>İlgili Etkinlik</Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => navigate(`/events/${item.relatedEntityId}`)}
                                            sx={{
                                                borderRadius: '5px',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                boxShadow: 'none',
                                                borderColor: '#0E4D94',
                                                color: '#0E4D94',
                                                '&:hover': {
                                                    borderColor: '#0E4D94',
                                                    backgroundColor: '#E7EDF4',
                                                    boxShadow: 'none',
                                                },
                                            }}
                                        >
                                            Etkinlik Detayına Git
                                        </Button>
                                    </Stack>
                                    {eventInfo ? (
                                        <Stack spacing={0.75}>
                                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>
                                                <Box component="span" sx={{ fontWeight: 500 }}>Başlık:</Box> {eventInfo.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>
                                                <Box component="span" sx={{ fontWeight: 500 }}>Tarih:</Box> {eventInfo.startDate ? formatTurkeyLocalDateTime(eventInfo.startDate) : ''} - {eventInfo.endDate ? formatTurkeyLocalDateTime(eventInfo.endDate) : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>
                                                <Box component="span" sx={{ fontWeight: 500 }}>Şehir:</Box> {eventInfo.location?.city || '-'}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#a3a3a3' }}>Etkinlik bilgisi yüklenemedi veya bulunamadı.</Typography>
                                    )}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Stack>
            </Card>

            <Divider sx={{ borderColor: '#e6e6e6' }} />
            <Stack spacing={0.5} sx={{ mt: -0.5 }}>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Oluşturan: {creatorLabel}
                    {item.createdAt ? ` • ${formatDate(item.createdAt)}` : ''}
                </Typography>
                {creatorError && (
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#F8285A' }}>{creatorError}</Typography>
                )}
            </Stack>

            <EditAnnouncementDialog
                open={editOpen}
                announcementId={item.id}
                onClose={() => setEditOpen(false)}
                onSaved={async () => {
                    setEditOpen(false);
                    // refresh detail after save
                    try {
                        const data = await fetchAnnouncementById(item.id);
                        setItem(data);
                        setCreatorInfo(null);
                        setCreatorError('');
                        if (data?.createdById) {
                            try {
                                const creator = await fetchUserById(data.createdById);
                                setCreatorInfo(creator);
                            } catch (err) {
                                setCreatorError(err?.message || 'Kullanıcı bilgisi alınamadı');
                            }
                        }
                        if (data?.category === 1 && data?.relatedEntityId) {
                            try { const ev = await fetchEventById(data.relatedEntityId); setEventInfo(ev); } catch { }
                        } else {
                            setEventInfo(null);
                        }
                    } catch { }
                }}
            />
        </Stack>
    );
};

export default AnnouncementDetail;



